import React from 'react'
import { useNavigate } from 'react-router-dom'
import { formatDistanceToNowStrict } from 'date-fns'
import { Bot, KeyRound, Settings } from 'lucide-react'

import SharedNotificationBell from '@/components/shared/NotificationBell'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { notificationService } from '@/services/notificationService'
import { cn } from '@/lib/utils'

// Project Central notifications: @mentions, assignments, comments and status changes on
// tasks you watch, API token expiry. Shown in the top bar of every sub-app.
const NotificationItem = ({ n }) => (
  <div className="flex gap-2.5">
    <Avatar className="h-7 w-7 shrink-0 mt-0.5">
      <AvatarFallback className="text-[11px] bg-primary/10 text-primary">
        {n.type === 'token_expiring' || n.type === 'app_connected' ? <KeyRound className="h-3.5 w-3.5" /> : (n.actor?.name || '?').charAt(0).toUpperCase()}
      </AvatarFallback>
    </Avatar>
    <div className="min-w-0 flex-1">
      <p className={cn('text-sm leading-snug', !n.read && 'font-medium')}>{n.title}</p>
      {n.body && <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{n.body}</p>}
      <p className="text-[11px] text-muted-foreground mt-1 flex items-center gap-1.5">
        {formatDistanceToNowStrict(new Date(n.created_at), { addSuffix: true })}
        {n.via && (
          <span className="inline-flex items-center gap-0.5 rounded bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300 px-1">
            <Bot className="h-3 w-3" /> via {n.via}
          </span>
        )}
      </p>
    </div>
    {!n.read && <span className="mt-2 h-2 w-2 shrink-0 rounded-full bg-primary" aria-label="unread" />}
  </div>
)

const TaskNotificationBell = () => {
  const navigate = useNavigate()

  return (
    <SharedNotificationBell
      queryKey="pc-notifications"
      fetchUnreadCount={async () => (await notificationService.getUnreadCount())?.data?.count || 0}
      fetchList={async () => (await notificationService.getNotifications({ limit: 30 }))?.data?.notifications || []}
      isUnread={(n) => !n.read}
      onMarkAllRead={notificationService.markAllRead}
      onSelect={async (n) => {
        if (!n.read) await notificationService.markRead([n.id])
        if (n.task) navigate(`/projects/${n.task.project_id}/tasks/${n.task.id}`)
        else if (n.type === 'token_expiring' || n.type === 'app_connected') navigate('/profile/api-tokens')
      }}
      renderItem={(n) => <NotificationItem n={n} />}
      emptyText="Nothing yet. You'll see @mentions, assignments, and updates on tasks you watch here."
      footer={({ close }) => (
        <button
          type="button"
          className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground"
          onClick={() => { close(); navigate('/profile/notifications') }}
        >
          <Settings className="h-3.5 w-3.5" /> Notification settings
        </button>
      )}
    />
  )
}

export default TaskNotificationBell
